(() => {
  const PRODUCT_FORM_SELECTOR = '[data-lapidopia-product-form]';
  const LINE_CHANGE_SELECTOR = '[data-lapidopia-cart-change]';
  const CART_DRAWER_SECTION_ID = 'lapidopia-cart-drawer';
  const LINE_CHANGE_DEBOUNCE_MS = 120;
  let mutationTail = Promise.resolve();
  let pendingAddedCount = 0;
  const pendingLineChanges = new Map();

  function fetcher() {
    if (typeof window.fetch === 'function') return window.fetch.bind(window);
    if (typeof fetch === 'function') return fetch;
    return null;
  }

  function routeRoot() {
    const configured = window.Shopify?.routes?.root || '/';
    return configured.endsWith('/') ? configured : `${configured}/`;
  }

  function route(path) {
    return `${routeRoot()}${String(path).replace(/^\/+/, '')}`;
  }

  async function request(path, init = {}) {
    const runFetch = fetcher();
    if (!runFetch) throw new Error('Your browser could not update the Bag.');
    const response = await runFetch(route(path), {
      ...init,
      headers: { Accept: 'application/json', ...(init.headers || {}) }
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.description || payload.message || 'Shopify could not update your Bag.');
    }
    return payload;
  }

  async function fetchCartState() {
    return request('cart.js', { method: 'GET' });
  }

  function updateHeaderCount(cartState) {
    document.querySelectorAll('[data-lapidopia-cart-count]').forEach(node => {
      node.textContent = String(cartState.item_count);
    });
  }

  function drawerItemCount(drawer = document.querySelector('[data-lapidopia-cart-drawer-root]')) {
    const itemCount = Number(drawer?.dataset?.lapidopiaCartItemCount);
    return Number.isInteger(itemCount) && itemCount >= 0 ? itemCount : null;
  }

  function updateOptimisticCartCount(
    drawer = document.querySelector('[data-lapidopia-cart-drawer-root]')
  ) {
    const serverCount = drawerItemCount(drawer);
    if (serverCount === null) return;
    let itemCount = serverCount + pendingAddedCount;
    for (const pending of pendingLineChanges.values()) {
      if (
        Number.isInteger(pending.confirmedQuantity)
        && Number.isInteger(pending.desiredQuantity)
      ) {
        itemCount += pending.desiredQuantity - pending.confirmedQuantity;
      }
    }
    updateHeaderCount({ item_count: Math.max(0, itemCount) });
  }

  function adjustDrawerItemCount(drawer, delta) {
    const itemCount = drawerItemCount(drawer);
    if (itemCount === null || !Number.isInteger(delta)) return;
    drawer.dataset.lapidopiaCartItemCount = String(Math.max(0, itemCount + delta));
  }

  function bundledSectionsUrl() {
    const pathname = window.location?.pathname || routeRoot();
    return `${pathname}${window.location?.search || ''}`;
  }

  function openCartDrawer(drawer, message) {
    const dialog = drawer?.querySelector?.('[data-lapidopia-cart-dialog]');
    const opener = document.querySelector('[data-lapidopia-bag-open]');
    window.LapidopiaDialog?.open?.(dialog, opener);
    writeMessage(drawer?.querySelector?.('[data-lapidopia-cart-message]'), message);
  }

  function replaceCartDrawer(next) {
    const current = document.querySelector('[data-lapidopia-cart-drawer-root]');
    if (!next || !current) throw new Error('The Bag response was incomplete.');
    const currentDialog = current.querySelector('[data-lapidopia-cart-dialog]');
    if (currentDialog?.open) {
      window.LapidopiaDialog?.close?.(currentDialog, { restoreFocus: false });
    }
    current.replaceWith(next);
    rebindPendingLineChanges(next);
    updateOptimisticCartCount(next);
    return next;
  }

  function renderBundledCart(payload, message) {
    const markup = payload?.sections?.[CART_DRAWER_SECTION_ID];
    if (typeof markup !== 'string' || !markup.trim()) {
      throw new Error('The Bag response was incomplete.');
    }
    const Parser = window.DOMParser || DOMParser;
    const next = new Parser().parseFromString(markup, 'text/html')
      .querySelector('[data-lapidopia-cart-drawer-root]');
    replaceCartDrawer(next);

    openCartDrawer(next, message);
    return next;
  }

  async function refreshCartDrawer() {
    const runFetch = fetcher();
    if (!runFetch) throw new Error('Your browser could not refresh the Bag.');
    const response = await runFetch(route('?section_id=lapidopia-cart-drawer'), {
      headers: { Accept: 'text/html' }
    });
    if (!response.ok) throw new Error('The Bag could not be refreshed.');
    const Parser = window.DOMParser || DOMParser;
    const parsed = new Parser().parseFromString(await response.text(), 'text/html');
    const next = parsed.querySelector('[data-lapidopia-cart-drawer-root]');
    return replaceCartDrawer(next);
  }

  function writeMessage(node, message) {
    if (!node) return;
    node.textContent = message;
    node.hidden = false;
  }

  function clearMessage(node) {
    if (!node) return;
    node.textContent = '';
    node.hidden = true;
  }

  function setBusy(target, control, busy) {
    if (busy) target?.setAttribute?.('aria-busy', 'true');
    else target?.removeAttribute?.('aria-busy');
    if (control) control.disabled = busy;
  }

  function enqueueMutation(operation) {
    const queued = mutationTail.then(operation);
    mutationTail = queued.catch(() => undefined);
    return queued;
  }

  function serializeMutation(target, control, operation) {
    setBusy(target, control, true);
    const queued = enqueueMutation(operation);
    return queued.finally(() => setBusy(target, control, false));
  }

  function displayedLineQuantity(line) {
    const node = line?.querySelector?.('[data-lapidopia-cart-quantity-value]');
    const quantity = Number(node?.textContent);
    return Number.isInteger(quantity) && quantity >= 0 ? quantity : null;
  }

  function applyLineQuantity(line, quantity) {
    const node = line?.querySelector?.('[data-lapidopia-cart-quantity-value]');
    if (!node) return;
    node.textContent = String(quantity);
    if (line.dataset) line.dataset.lapidopiaCartQuantity = String(quantity);
    line.querySelectorAll?.(LINE_CHANGE_SELECTOR).forEach(control => {
      const delta = Number(control.dataset?.lapidopiaCartDelta);
      if (!Number.isInteger(delta)) return;
      control.dataset.quantity = String(Math.max(0, quantity + delta));
    });
  }

  function setLineControlsDisabled(line, disabled) {
    line?.querySelectorAll?.(LINE_CHANGE_SELECTOR).forEach(control => {
      control.disabled = disabled;
    });
  }

  function findCartLine(drawer, itemKey) {
    return Array.from(drawer?.querySelectorAll?.('[data-lapidopia-cart-line]') || [])
      .find(line => line.dataset?.lapidopiaCartKey === itemKey) || null;
  }

  function rebindPendingLineChanges(drawer) {
    for (const pending of pendingLineChanges.values()) {
      const line = findCartLine(drawer, pending.itemKey);
      if (!line) continue;
      pending.line = line;
      pending.error = line.querySelector?.('[data-lapidopia-cart-line-message]')
        || drawer.querySelector?.('[data-lapidopia-cart-message]');
      if (pending.desiredQuantity > 0) {
        applyLineQuantity(line, pending.desiredQuantity);
      } else {
        setLineControlsDisabled(line, true);
      }
      setBusy(line, null, true);
    }
  }

  function settleLineWaiters(pending, method, value) {
    const waiters = pending.waiters.splice(0);
    waiters.forEach(waiter => waiter[method](value));
  }

  function scheduleLineChange(pending, delay = LINE_CHANGE_DEBOUNCE_MS) {
    if (pending.inFlight) return;
    if (pending.timer) clearTimeout(pending.timer);
    pending.timer = setTimeout(() => {
      pending.timer = null;
      flushLineChange(pending);
    }, delay);
  }

  async function flushLineChange(pending) {
    if (pending.inFlight) return;
    pending.inFlight = true;
    const sentQuantity = pending.desiredQuantity;
    const previousConfirmedQuantity = pending.confirmedQuantity;
    try {
      const payload = await enqueueMutation(() => request('cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: pending.itemKey,
          quantity: sentQuantity,
          sections: CART_DRAWER_SECTION_ID,
          sections_url: bundledSectionsUrl()
        })
      }));
      pending.confirmedQuantity = sentQuantity;
      const result = await renderMutationResult(
        payload,
        sentQuantity === 0 ? 'Item removed from your Bag.' : 'Bag updated.'
      );
      const drawer = result.drawer;
      pending.synchronized = result.synchronized;
      if (result.synchronized === false) {
        adjustDrawerItemCount(
          drawer,
          sentQuantity - previousConfirmedQuantity
        );
      }
      updateOptimisticCartCount(drawer);
      pending.inFlight = false;

      if (pending.desiredQuantity !== sentQuantity) {
        pending.line = findCartLine(drawer, pending.itemKey) || pending.line;
        applyLineQuantity(pending.line, pending.desiredQuantity);
        updateOptimisticCartCount(drawer);
        scheduleLineChange(pending, 0);
        return;
      }

      pendingLineChanges.delete(pending.itemKey);
      updateOptimisticCartCount(drawer);
      settleLineWaiters(pending, 'resolve', drawer);
    } catch (errorObject) {
      pending.inFlight = false;
      pendingLineChanges.delete(pending.itemKey);
      applyLineQuantity(pending.line, pending.confirmedQuantity);
      updateOptimisticCartCount();
      writeMessage(pending.error, errorObject.message);
      settleLineWaiters(pending, 'reject', errorObject);
    } finally {
      if (!pendingLineChanges.has(pending.itemKey)) {
        setBusy(pending.line, null, false);
        if (sentQuantity !== 0 || pending.synchronized !== false) {
          setLineControlsDisabled(pending.line, false);
        }
      }
    }
  }

  async function completeMutation(message) {
    const state = await fetchCartState();
    const current = document.querySelector('[data-lapidopia-cart-drawer-root]');
    if (current?.dataset) {
      current.dataset.lapidopiaCartItemCount = String(state.item_count);
      updateOptimisticCartCount(current);
    }
    const drawer = await refreshCartDrawer();
    const dialog = drawer.querySelector('[data-lapidopia-cart-dialog]');
    const opener = document.querySelector('[data-lapidopia-bag-open]');
    window.LapidopiaDialog?.open?.(dialog, opener);
    writeMessage(drawer.querySelector('[data-lapidopia-cart-message]'), message);
    return drawer;
  }

  async function renderMutationResult(payload, message) {
    const markup = payload?.sections?.[CART_DRAWER_SECTION_ID];
    if (typeof markup === 'string' && markup.trim()) {
      try {
        return { drawer: renderBundledCart(payload, message), synchronized: true };
      } catch {
        const current = document.querySelector('[data-lapidopia-cart-drawer-root]');
        openCartDrawer(current, `${message} Refresh the page to see the latest Bag details.`);
        return { drawer: current, synchronized: false };
      }
    }

    try {
      return { drawer: await completeMutation(message), synchronized: true };
    } catch {
      const current = document.querySelector('[data-lapidopia-cart-drawer-root]');
      openCartDrawer(current, `${message} Refresh the page to see the latest Bag details.`);
      return { drawer: current, synchronized: false };
    }
  }

  function addProduct(form) {
    const error = form?.querySelector?.('[data-lapidopia-product-error]');
    const submit = form?.querySelector?.('[type="submit"]');
    const FormDataConstructor = window.FormData || FormData;
    const body = new FormDataConstructor(form);
    body.set('sections', CART_DRAWER_SECTION_ID);
    body.set('sections_url', bundledSectionsUrl());
    const requestedQuantity = Number(body.get('quantity'));
    const addedQuantity = Number.isInteger(requestedQuantity) && requestedQuantity > 0
      ? requestedQuantity
      : 1;
    let countIsPending = true;
    pendingAddedCount += addedQuantity;
    updateOptimisticCartCount();
    clearMessage(error);
    openCartDrawer(
      document.querySelector('[data-lapidopia-cart-drawer-root]'),
      'Adding to your Bag…'
    );
    return serializeMutation(form, submit, async () => {
      try {
        const payload = await request('cart/add.js', { method: 'POST', body });
        pendingAddedCount -= addedQuantity;
        countIsPending = false;
        const result = await renderMutationResult(payload, 'Added to your Bag.');
        if (result.synchronized === false) {
          adjustDrawerItemCount(result.drawer, addedQuantity);
        }
        updateOptimisticCartCount(result.drawer);
        return result.drawer;
      } catch (errorObject) {
        if (countIsPending) {
          pendingAddedCount -= addedQuantity;
          countIsPending = false;
          updateOptimisticCartCount();
        }
        writeMessage(error, errorObject.message);
        openCartDrawer(
          document.querySelector('[data-lapidopia-cart-drawer-root]'),
          errorObject.message
        );
        throw errorObject;
      }
    });
  }

  function changeLine(itemKey, quantity, control) {
    const line = control?.closest?.('[data-lapidopia-cart-line]');
    const error = line?.querySelector?.('[data-lapidopia-cart-line-message]')
      || document.querySelector('[data-lapidopia-cart-message]');
    const nextQuantity = Number(quantity);
    if (!Number.isInteger(nextQuantity) || nextQuantity < 0) {
      const errorObject = new Error('Choose a whole-number quantity.');
      writeMessage(error, errorObject.message);
      return Promise.reject(errorObject);
    }

    clearMessage(error);
    let pending = pendingLineChanges.get(itemKey);
    if (!pending) {
      pending = {
        confirmedQuantity: displayedLineQuantity(line),
        desiredQuantity: nextQuantity,
        error,
        inFlight: false,
        itemKey,
        line,
        timer: null,
        waiters: []
      };
      pendingLineChanges.set(itemKey, pending);
    } else {
      pending.desiredQuantity = nextQuantity;
      pending.error = error;
      pending.line = line || pending.line;
    }

    if (nextQuantity > 0) applyLineQuantity(pending.line, nextQuantity);
    else setLineControlsDisabled(pending.line, true);
    setBusy(pending.line, null, true);
    updateOptimisticCartCount();

    const promise = new Promise((resolve, reject) => {
      pending.waiters.push({ resolve, reject });
    });
    scheduleLineChange(pending, LINE_CHANGE_DEBOUNCE_MS);
    return promise;
  }

  function closest(node, selector) {
    if (node?.matches?.(selector)) return node;
    return node?.closest?.(selector) || null;
  }

  document.addEventListener('submit', event => {
    const form = closest(event.target, PRODUCT_FORM_SELECTOR);
    if (!form || !fetcher()) return;
    event.preventDefault();
    return addProduct(form).catch(() => undefined);
  });

  document.addEventListener('click', event => {
    const control = closest(event.target, LINE_CHANGE_SELECTOR);
    if (!control || !fetcher()) return;
    event.preventDefault();
    return changeLine(control.dataset.key, Number(control.dataset.quantity), control).catch(() => undefined);
  });

  window.LapidopiaCart = Object.freeze({
    addProduct,
    changeLine,
    fetchCartState,
    refreshCartDrawer
  });
})();
